import { createHash } from "node:crypto";
import { execFile } from "node:child_process";
import { createReadStream } from "node:fs";
import * as fs from "node:fs/promises";
import * as os from "node:os";
import * as path from "node:path";
import {
  companionTargetFor,
  releaseAssetUrl,
  type CompanionComponent,
  type CompanionTarget,
} from "@m8t-stack/platform-release";
import { LocalCliError } from "./errors.js";

/** Larger than any build published so far, and bounded so a wrong URL cannot fill a disk. */
const MAX_ASSET_BYTES = 512 * 1024 * 1024;

/**
 * Three tries at the bytes, then the partial waits for the next command.
 *
 * The install this runs inside is driven by a coding agent reading a runbook,
 * so a connection that blips at 119 MB has to heal without anyone noticing it
 * happened. Three is enough for a blip and few enough that a link which is
 * genuinely down still fails while the founder is watching.
 */
const MAX_ATTEMPTS = 3;
const BACKOFF_MS = [1_000, 3_000];

/**
 * Why an attempt is being made again. Three genuinely different things happen
 * to a founder, and one line cannot describe all three truthfully: the body
 * failed part way through, the request never reached a server at all, or the
 * server answered and refused. A line has to be true on every path that renders
 * it, or it needs its own line.
 */
export type CompanionRetryCause = "dropped" | "not-connected" | "server-error";

export type CompanionDownloadEvent =
  /** A partial was found on disk and the transfer picked up from it. */
  | { readonly kind: "resumed"; readonly received: number; readonly total: number | null }
  | { readonly kind: "progress"; readonly received: number; readonly total: number | null }
  /** An attempt failed with `received` bytes safe on disk. */
  | {
      readonly kind: "retrying";
      readonly received: number;
      readonly attempt: number;
      readonly cause: CompanionRetryCause;
    };

/** Shared with the renderer so one number is never formatted two ways. */
export function megabytes(bytes: number): string {
  return `${Math.round(bytes / 1e6).toString()} MB`;
}

export interface DownloadedCompanion {
  readonly version: string;
  /** Where the artifact manifest landed — what installCompanion is pointed at. */
  readonly artifactManifestPath: string;
  /** Removes the unpacked copy. Safe to call more than once. */
  readonly dispose: () => Promise<void>;
}

interface DownloadDeps {
  readonly fetchImpl?: typeof fetch;
  readonly makeTemporaryDirectory?: () => Promise<string>;
  readonly extract?: (archive: string, into: string) => Promise<void>;
  /** The host to fetch for. Named rather than read, so a test pins it. */
  readonly platform?: NodeJS.Platform;
  readonly architecture?: string;
  /**
   * Where an interrupted download waits for the next attempt. Injected so no
   * test reads or writes the real `~/.m8t`, which on this machine points at a
   * live install.
   */
  readonly downloadDirectory?: string;
  readonly onEvent?: (event: CompanionDownloadEvent) => void;
  /** Injected so the backoff between attempts does not make a suite sleep. */
  readonly delay?: (ms: number) => Promise<void>;
  /** The bound itself, so the guard that enforces it can be shown to fail. */
  readonly maxAssetBytes?: number;
}

function extractArchive(archive: string, into: string): Promise<void> {
  return new Promise((resolve, reject) => {
    // The bytes were held to their pinned digest before this ran, so what is
    // being unpacked is the release's own build and nothing else.
    execFile("tar", ["-xzf", archive, "-C", into], { windowsHide: true }, (error) => {
      if (error) reject(new Error(`Could not unpack the companion build: ${error.message}`));
      else resolve();
    });
  });
}

/**
 * Where a download that did not finish waits.
 *
 * Under the companion's own state directory rather than a scratch dir, because
 * the whole value of keeping it is that it is still there on the next command,
 * possibly after a reboot.
 */
export function companionDownloadDirectory(homeDirectory: string): string {
  return path.join(homeDirectory, ".m8t", "companion", "downloads");
}

/**
 * The build for this machine, or a reason there is none.
 *
 * Kept separate from the download so `status` can say what is available
 * without fetching several hundred megabytes to find out.
 */
export function companionAssetFor(
  component: CompanionComponent,
  platform: NodeJS.Platform = process.platform,
  architecture: string = process.arch,
): { target: CompanionTarget; asset: string; sha256: string } | null {
  const target = companionTargetFor(platform, architecture);
  if (target === null) return null;
  const pinned = component.targets[target];
  return pinned ? { target, asset: pinned.asset, sha256: pinned.sha256 } : null;
}

function tooLarge(asset: string): LocalCliError {
  return new LocalCliError({
    code: "COMPANION_ASSET_TOO_LARGE",
    message: `${asset} is larger than this command will accept.`,
  });
}

function parseContentRange(
  value: string | null,
): { readonly start: number; readonly total: number | null } | null {
  const matched = /^bytes (\d+)-(\d+)\/(\d+|\*)$/u.exec((value ?? "").trim());
  if (!matched?.[1] || !matched[3]) return null;
  return {
    start: Number(matched[1]),
    total: matched[3] === "*" ? null : Number(matched[3]),
  };
}

function declaredLength(response: Response): number | null {
  const raw = response.headers.get("content-length");
  if (raw === null) return null;
  const value = Number(raw);
  return Number.isSafeInteger(value) && value >= 0 ? value : null;
}

/**
 * How many bytes of this asset are already on disk and safe to build on.
 *
 * Anything that is not a plain file is removed rather than trusted: the
 * downloader owns this path, and a directory or a symlink standing in it is
 * something it did not put there.
 */
async function usableBytes(partial: string): Promise<number> {
  const status = await fs.lstat(partial).catch(() => null);
  if (status === null) return 0;
  if (!status.isFile()) {
    await fs.rm(partial, { recursive: true, force: true }).catch(() => undefined);
    // Not cleanup — this is the precondition for opening the same path for
    // writing a moment later, with no O_NOFOLLOW. A removal that quietly failed
    // would leave a symlink standing here and send ~120 MB through it to
    // wherever it points. So the removal is confirmed rather than assumed.
    if (await fs.lstat(partial).then(() => true, () => false)) {
      throw new LocalCliError({
        code: "COMPANION_DOWNLOAD_PATH_OCCUPIED",
        message: `${partial} is not a file and could not be cleared.`,
        hint: "Something that is not a download is sitting where the companion build goes. Remove it, then run this again.",
      });
    }
    return 0;
  }
  return status.size;
}

/**
 * Remove partials bound to some other build.
 *
 * A machine wants one companion at a time, so bytes bound to a digest this
 * command will never ask for again are dead weight — and they are bound by
 * NAME, which is why a stale partial can never be resumed into a build it does
 * not belong to. The binding is structural rather than checked.
 */
async function sweepForeignPartials(directory: string, keep: string): Promise<void> {
  const entries = await fs.readdir(directory).catch(() => [] as string[]);
  await Promise.all(
    entries
      .filter((name) => name.endsWith(".part") && name !== keep)
      .map((name) => fs.rm(path.join(directory, name), { recursive: true, force: true })),
  );
}

function sha256File(filePath: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const hash = createHash("sha256");
    const stream = createReadStream(filePath);
    stream.on("error", reject);
    // Fed a chunk at a time: the asset is never held whole in memory, and what
    // is hashed is what actually LANDED rather than what was believed written.
    stream.on("data", (chunk) => hash.update(chunk));
    stream.on("end", () => { resolve(hash.digest("hex")); });
  });
}

/**
 * Fetch the asset to `partial`, picking up from whatever is already there.
 *
 * Returns when the transfer completed. Nothing here vouches for the bytes —
 * the digest does that, once, afterwards, however many attempts delivered them.
 */
async function transferToDisk(input: {
  readonly url: string;
  readonly asset: string;
  readonly partial: string;
  readonly fetchImpl: typeof fetch;
  readonly onEvent: (event: CompanionDownloadEvent) => void;
  readonly delay: (ms: number) => Promise<void>;
  readonly maxAssetBytes: number;
  /** The digest the release pins, so an already-complete partial is recognised. */
  readonly expectedDigest: string;
}): Promise<void> {
  let lastError: unknown;
  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
    let existing = await usableBytes(input.partial);
    if (existing > input.maxAssetBytes) {
      await fs.rm(input.partial, { force: true }).catch(() => undefined);
      existing = 0;
    }
    // `received` lives out here because the retry tail reports it, and the
    // request itself can fail before there is anything else to report.
    let received = existing;
    // Which of the two remaining causes this attempt failed for. Set the moment
    // a response exists, so the catch can tell "never reached a server" from
    // "the body stopped part way".
    let answered = false;
    try {
      const controller = new AbortController();
      // Inside the try, deliberately. A connection refused, a DNS failure or a
      // TLS error REJECTS rather than answering, and those are the commonest
      // transients there are — left outside, the bounded retry covered only the
      // failures polite enough to send response headers first. Header parsing
      // and the pre-stream checks came in with it, so an unexpected throw there
      // now costs three attempts instead of surfacing at once.
      const response = await input.fetchImpl(input.url, {
        signal: controller.signal,
        ...(existing > 0 ? { headers: { Range: `bytes=${existing.toString()}-` } } : {}),
      });

      answered = true;

      if (response.status === 416) {
        await response.body?.cancel().catch(() => undefined);
        // "At or past the end" includes EXACTLY the end. The partial survives the
        // digest pass and the whole unpack, so a run killed in that window leaves
        // a complete, correct file — and asking for `bytes=<size>-` is
        // unsatisfiable, which is indistinguishable from a partial that has
        // outlived its asset. Ask the digest which one this is before throwing
        // away 120 MB that was already paid for.
        if (existing > 0 && (await sha256File(input.partial).catch(() => null)) === input.expectedDigest) {
          return;
        }
        await fs.rm(input.partial, { force: true }).catch(() => undefined);
        lastError = new Error("the interrupted download no longer fits the published asset");
        continue;
      }
      if (!response.ok) {
        await response.body?.cancel().catch(() => undefined);
        const failed = new LocalCliError({
          code: "COMPANION_ASSET_FETCH_FAILED",
          message: `GET ${input.url} returned HTTP ${response.status.toString()}.`,
          hint: "The release the platform is pinned to should carry this file. Try again, or run 'm8t companion repair' once the network is back.",
        });
        // A 5xx is the CDN having a moment, which is exactly the blip the
        // bounded retry exists to survive. The reason the other statuses are
        // terminal is that a 404 will not heal — and that reasoning is about
        // 404, not about every code that is not 200.
        if (response.status < 500) throw failed;
        lastError = failed;
        if (attempt + 1 < MAX_ATTEMPTS) {
          input.onEvent({ kind: "retrying", received: existing, attempt: attempt + 1, cause: "server-error" });
          await input.delay(BACKOFF_MS[Math.min(attempt, BACKOFF_MS.length - 1)]);
        }
        continue;
      }

      let offset: number;
      let total: number | null;
      if (response.status === 206) {
        const range = parseContentRange(response.headers.get("content-range"));
        if (range?.start !== existing) {
          // A range answered from somewhere other than where it was asked for
          // would append the wrong bytes. Start clean instead.
          await response.body?.cancel().catch(() => undefined);
          await fs.rm(input.partial, { force: true }).catch(() => undefined);
          lastError = new Error("the server answered a different range than the one requested");
          continue;
        }
        offset = existing;
        // `content-range: bytes 5-9/*` declares no total, and none is worked out
        // from `offset + content-length`: servers are known to answer a 206 with
        // the FULL length there rather than the remaining length, which makes
        // that sum too big. A total is used for three things — refusing an
        // oversized asset, deciding a transfer stopped short, and telling the
        // founder a percentage — and a guess is not good enough for any of them.
        // Unknown is the honest answer: the running byte count still refuses an
        // oversized body, the digest still refuses a short one, and the founder
        // is shown megabytes instead of a percentage that might be a fiction.
        total = range.total;
      } else {
        // A 200 to a ranged request means the server ignored the range and is
        // sending the whole file. Appending it onto the partial is exactly how a
        // resume corrupts a download, so the partial is overwritten instead.
        offset = 0;
        total = declaredLength(response);
      }
      // Two ways to know before reading a byte. The total is the whole asset. The
      // body's own declared length is only the part being sent — but a PART that
      // exceeds the bound puts the whole past it too, whichever way the server
      // meant its header, so this one needs no arithmetic and holds on the 206
      // path where the total is unknown. Without it that path has a single leg,
      // and a bound with one leg is a bound that can stop being checked.
      const declaredBody = declaredLength(response);
      if (
        (total !== null && total > input.maxAssetBytes) ||
        (declaredBody !== null && declaredBody > input.maxAssetBytes)
      ) {
        await response.body?.cancel().catch(() => undefined);
        // Bytes that provably cannot verify are not worth keeping — and keeping
        // them means resuming into the same refusal on every later run, with the
        // excess parked in the founder's home directory for good.
        await fs.rm(input.partial, { force: true }).catch(() => undefined);
        throw tooLarge(input.asset);
      }
      if (offset > 0) input.onEvent({ kind: "resumed", received: offset, total });

      received = offset;
      const body = response.body;
      if (body === null) throw new Error("the response carried no body");
      const handle = await fs.open(input.partial, offset > 0 ? "a" : "w", 0o600);
      try {
        for await (const chunk of body as AsyncIterable<Uint8Array>) {
          received += chunk.byteLength;
          if (received > input.maxAssetBytes) {
            controller.abort();
            throw tooLarge(input.asset);
          }
          await handle.write(chunk);
          input.onEvent({ kind: "progress", received, total });
        }
      } finally {
        await handle.close();
      }
      // A body that stopped short is a broken transfer, not a broken build.
      // Left to the digest it would be reported as a corrupted or substituted
      // file — the security message — and the bytes thrown away, when what it
      // needs is another attempt at the rest.
      //
      // This runs only where the server stated a total outright. Where it did
      // not, there is no honest way to tell a short body from a complete one
      // and the digest is the backstop: safe, but it costs the founder the
      // whole download and tells them it looked substituted. Recorded, not
      // solved — and not reachable on the CDN the release channel actually
      // uses, which always states a total.
      if (total !== null && received < total) {
        throw new Error(
          `the transfer stopped at ${received.toString()} of ${total.toString()} bytes`,
        );
      }
      return;
    } catch (error) {
      // A bound that has been exceeded is not a network problem, and retrying
      // it would only exceed it again.
      if (error instanceof LocalCliError) {
        if (error.code === "COMPANION_ASSET_TOO_LARGE") {
          await fs.rm(input.partial, { force: true }).catch(() => undefined);
        }
        throw error;
      }
      lastError = error;
      if (attempt + 1 < MAX_ATTEMPTS) {
        input.onEvent({
          kind: "retrying",
          received,
          attempt: attempt + 1,
          cause: answered ? "dropped" : "not-connected",
        });
        await input.delay(BACKOFF_MS[Math.min(attempt, BACKOFF_MS.length - 1)]);
      }
    }
  }
  // What is actually on disk, asked of the disk. Every attempt may have added
  // to it, and the founder is about to be told this number.
  const kept = await fs.lstat(input.partial).then(
    (status) => (status.isFile() ? status.size : 0),
    () => 0,
  );
  // Gated on what the sentence would SAY, not on the byte count: the copy names
  // an amount, and anything under half a megabyte renders as "The 0 MB already
  // downloaded is kept", which reassures nobody. Asked of the formatter rather
  // than re-derived from it, so the two cannot come to disagree.
  const keptLabel = megabytes(kept);
  throw new LocalCliError({
    code: "COMPANION_DOWNLOAD_INCOMPLETE",
    message: "The companion build could not be downloaded.",
    ...(keptLabel === "0 MB"
      ? {}
      : { hint: `The ${keptLabel} already downloaded is kept. Run it again to carry on from there.` }),
    cause: lastError,
  });
}

/**
 * Fetch the companion build the release pins for this machine and unpack it.
 *
 * The asset streams to disk rather than into memory, and what does not arrive
 * is asked for again from where it stopped — the build is ~120 MB and it is the
 * longest single step of an install, so a connection that drops near the end
 * used to cost all of it. However the bytes arrived, they are held to the
 * digest in the manifest before anything is unpacked, so a substituted archive
 * never reaches tar. What comes out is then verified again, file by file, by
 * the install itself.
 */
export async function downloadCompanionArtifact(
  component: CompanionComponent,
  deps: DownloadDeps = {},
): Promise<DownloadedCompanion> {
  const platform = deps.platform ?? process.platform;
  const architecture = deps.architecture ?? process.arch;
  const pinned = companionAssetFor(component, platform, architecture);
  if (pinned === null) {
    throw new LocalCliError({
      code: "COMPANION_NO_BUILD_FOR_HOST",
      message: `Release ${component.version} carries no desktop companion for ${platform}-${architecture}.`,
      hint: "The companions are built for macOS and Windows on arm64 and x64.",
    });
  }
  const url = releaseAssetUrl(component.tag, pinned.asset);
  const directory = deps.downloadDirectory ?? companionDownloadDirectory(os.homedir());
  const partialName = `${pinned.sha256}.part`;
  const partial = path.join(directory, partialName);
  await fs.mkdir(directory, { recursive: true, mode: 0o700 });
  await sweepForeignPartials(directory, partialName);

  await transferToDisk({
    url,
    asset: pinned.asset,
    partial,
    fetchImpl: deps.fetchImpl ?? fetch,
    onEvent: deps.onEvent ?? (() => undefined),
    delay: deps.delay ?? ((ms) => new Promise((resolve) => setTimeout(resolve, ms))),
    maxAssetBytes: deps.maxAssetBytes ?? MAX_ASSET_BYTES,
    expectedDigest: pinned.sha256,
  });

  const digest = await sha256File(partial);
  if (digest !== pinned.sha256) {
    // Kept, it would be resumed from on every later attempt, and a partial that
    // can never verify is a failure the founder has no way to clear. Guarded so
    // a removal that fails cannot replace the diagnosis with its own error —
    // this is the one message that says the bytes were not what was published.
    await fs.rm(partial, { force: true }).catch(() => undefined);
    throw new LocalCliError({
      code: "COMPANION_ASSET_DIGEST_MISMATCH",
      message: `${pinned.asset} does not match the digest the release pins for it.`,
      hint: "Nothing was unpacked. This is what a corrupted download or a substituted file looks like — retry, and report it if it persists.",
    });
  }

  const root = await (deps.makeTemporaryDirectory ??
    (() => fs.mkdtemp(path.join(os.tmpdir(), "m8t-companion-"))))();
  const dispose = async (): Promise<void> => {
    await fs.rm(root, { recursive: true, force: true }).catch(() => undefined);
  };
  try {
    const unpacked = path.join(root, "unpacked");
    await fs.mkdir(unpacked, { recursive: false, mode: 0o700 });
    // tar reads the verified file where it lies: `~/.m8t` and the temp root can
    // be different filesystems, and moving 120 MB across one to save nothing is
    // a copy nobody asked for.
    await (deps.extract ?? extractArchive)(partial, unpacked);
    return {
      version: component.version,
      artifactManifestPath: path.join(unpacked, "artifact-v1.json"),
      dispose,
    };
  } catch (error) {
    await dispose();
    throw error;
  } finally {
    // Verified bytes tar could not read are not worth keeping either: the
    // plausible reason an unpack fails is a disk with no room left on it.
    await fs.rm(partial, { force: true }).catch(() => undefined);
  }
}
